import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";

const chartWidth = 880;
const chartHeight = 320;
const chartPadding = { top: 24, right: 24, bottom: 44, left: 64 };

const getNetWorthYAxisStep = (maxValue) => {
  if (maxValue <= 1000000) return 100000;
  if (maxValue <= 5000000) return 500000;
  return 1000000;
};

const formatCompactAxisValue = (value) => {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return Number.isInteger(millions) ? `${millions}m` : `${millions.toFixed(1)}m`;
  }
  if (value >= 1000) {
    const thousands = value / 1000;
    return Number.isInteger(thousands) ? `${thousands}k` : `${thousands.toFixed(1)}k`;
  }
  return String(value);
};

const ReportsNetWorth = () => {
  const [rows, setRows] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const [reportRes, currenciesRes] = await Promise.all([
          apiClient.get("/reports/net-worth"),
          apiClient.get("/currencies"),
        ]);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        setRows(Array.isArray(reportRes.data) ? reportRes.data : []);
        setCurrencies(currencyRows);
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load net worth report.");
        setRows([]);
        setCurrencies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const chartData = useMemo(() => {
    const sortedRows = [...rows].sort((a, b) => {
      const left = String(a.DateEnd || "");
      const right = String(b.DateEnd || "");
      if (left !== right) return left.localeCompare(right);
      return Number(a.PeriodId || 0) - Number(b.PeriodId || 0);
    });

    const points = sortedRows.map((row) => ({
      label: row.Description || `Period ${row.PeriodId}`,
      value: Number(row.NetWorthAmount || 0),
    }));

    const rawMaxValue = points.reduce((max, point) => Math.max(max, point.value), 0);
    const yAxisStep = getNetWorthYAxisStep(rawMaxValue);
    const maxValue = rawMaxValue > 0 ? Math.ceil(rawMaxValue / yAxisStep) * yAxisStep : yAxisStep;
    const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
    const innerHeight = chartHeight - chartPadding.top - chartPadding.bottom;
    const barGap = points.length > 1 ? 6 : 0;
    const barWidth = points.length > 0 ? Math.max(10, Math.min(28, (innerWidth - barGap * Math.max(points.length - 1, 0)) / points.length)) : 0;
    const scaleY = maxValue > 0 ? innerHeight / maxValue : 0;

    const bars = points.map((point, index) => {
      const height = point.value * scaleY;
      const x = chartPadding.left + index * (barWidth + barGap);
      const y = chartHeight - chartPadding.bottom - height;
      return {
        x,
        y,
        height,
        width: barWidth,
        centerX: x + barWidth / 2,
        value: point.value,
      };
    });

    const tickValues = [];
    for (let tick = 0; tick <= maxValue; tick += yAxisStep) {
      tickValues.push(tick);
    }

    return {
      points,
      maxValue,
      tickValues,
      bars,
      innerHeight,
    };
  }, [rows]);

  const summary = useMemo(() => {
    if (rows.length === 0) return null;
    const sortedRows = [...rows].sort((a, b) => {
      const left = String(a.DateEnd || "");
      const right = String(b.DateEnd || "");
      if (left !== right) return left.localeCompare(right);
      return Number(a.PeriodId || 0) - Number(b.PeriodId || 0);
    });
    const latest = [...sortedRows].reverse().find((row) => Number(row.NetWorthAmount || 0) > 0) || sortedRows[sortedRows.length - 1];
    const latestValue = Number(latest?.NetWorthAmount || 0);
    const usdCurrency = currencies.find((row) => String(row.Description || "").trim().toUpperCase() === "USD");
    const usdRate = Number(usdCurrency?.ExchangeRateToDefault || 0);
    const latestValueUsd =
      String(defaultCurrency || "").trim().toUpperCase() === "USD"
        ? latestValue
        : usdRate > 0
          ? latestValue / usdRate
          : null;

    return {
      latestPeriod: latest?.Description || "-",
      latestValue,
      latestValueUsd,
    };
  }, [rows, currencies, defaultCurrency]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Net Worth Report</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Net Worth
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading net worth report...</p>}

      {!loading && (
        <section className="panel">
          {chartData.points.length === 0 ? (
            <p className="notice notice-muted">No net worth data found.</p>
          ) : (
            <>
              <p className="notice notice-muted report-context-note">
                Shown in default currency: {defaultCurrency || "Default"}
              </p>

              <div className="dashboard-grid report-summary-strip">
                <div className="metric-card metric-card-compact">
                  <strong>Latest Net Worth</strong>
                  <div className="resource-meta">{formatMoney(summary?.latestValue || 0, defaultCurrency || "Default")}</div>
                </div>
                <div className="metric-card metric-card-compact">
                  <strong>Latest Net Worth In USD</strong>
                  <div className="resource-meta">
                    {summary?.latestValueUsd == null ? "USD rate not available" : formatMoney(summary.latestValueUsd, "USD")}
                  </div>
                </div>
              </div>

              <div className="line-chart-legend">
                <span className="line-chart-legend-item"><span className="line-chart-dot line-chart-dot-credit" /> Net worth by period</span>
              </div>

              <div className="table-scroll">
                <svg
                  className="line-chart"
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  role="img"
                  aria-label="Bar chart showing net worth for each reporting period"
                >
                  {chartData.tickValues.map((tick) => {
                    const y = chartHeight - chartPadding.bottom - (chartData.maxValue > 0 ? (tick / chartData.maxValue) * chartData.innerHeight : 0);
                    return (
                      <g key={tick}>
                        <line className="line-chart-grid" x1={chartPadding.left} y1={y} x2={chartWidth - chartPadding.right} y2={y} />
                        <text className="line-chart-axis-text" x={chartPadding.left - 10} y={y + 4} textAnchor="end">
                          {formatCompactAxisValue(tick)}
                        </text>
                      </g>
                    );
                  })}

                  <line className="line-chart-axis" x1={chartPadding.left} y1={chartPadding.top} x2={chartPadding.left} y2={chartHeight - chartPadding.bottom} />
                  <line className="line-chart-axis" x1={chartPadding.left} y1={chartHeight - chartPadding.bottom} x2={chartWidth - chartPadding.right} y2={chartHeight - chartPadding.bottom} />

                  {chartData.bars.map((bar, index) => (
                    <g key={`networth-${chartData.points[index].label}`}>
                      <rect
                        className="net-worth-bar"
                        x={bar.x}
                        y={bar.y}
                        width={bar.width}
                        height={bar.height}
                        rx="6"
                      />
                    </g>
                  ))}

                  {chartData.points.map((point, index) => {
                    const showLabel =
                      index === 0 ||
                      index === chartData.points.length - 1 ||
                      index % 5 === 0;
                    if (!showLabel) return null;
                    return (
                      <text
                        key={`label-${point.label}`}
                        className="line-chart-axis-text"
                        x={chartData.bars[index].centerX}
                        y={chartHeight - 16}
                        textAnchor="middle"
                      >
                        {point.label}
                      </text>
                    );
                  })}
                </svg>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ReportsNetWorth;
